import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Userback from '@userback/widget';
import { authService } from './services/authService';
import { USERBACK_TOKEN, SIGNALCUE_ENV } from './config';

// UserbackProvider loads the feedback widget so a partner on staging can file a
// bug from the screen it happened on.
//
// ONE EFFECT, keyed on location, doing two things:
//
//   - initialisation, once.
//   - identify() when the session appears. A SAME-ORIGIN login does not reload
//     the page (Login.jsx uses navigate() when APP_URL shares the browser's
//     origin, which is staging's case), so the moment a session becomes
//     readable is a route change -- which is why the effect is keyed on
//     location even though nothing per-route is sent.
//
// It deliberately does NOT call refresh() on route change, which the vendor's
// SPA guidance asks for. See the long comment at the call site below: doing so
// destroys the launcher.
//
// NO TEARDOWN ON LOGOUT, and this is verified rather than assumed:
// Layout.handleLogout ends in window.location.href and
// authService.handleSessionExpired in window.location.assign. Both are full
// document navigations, so the widget and its identity die with the page. A
// destroy() call here would be dead code that looks load-bearing.
//
// Spec: docs/superpowers/specs/2026-08-10-userback-feedback-widget-design.md
//
// identityOf builds what a report says about WHO FILED IT -- and nothing about
// what they were looking at.
//
// THE FIELD NAMES DIFFER ON BOTH SIDES. Ours are `user_id` and `display_name`
// (userJSON in cmd/api/auth.go:77-85, which serialises both /api/auth/login and
// /api/auth/me); Userback's are `id` and `name`. Writing user.id would send
// undefined, and Userback accepts that silently -- the reports would simply be
// attributed to nobody, with nothing failing to say so.
//
// NOTHING FROM app.* GOES HERE. No candidate, company, job, person or
// employment record; no row ids; no counts. A report carries who reported and,
// if they chose to attach one, a screenshot -- never what they were looking at
// as structured data. That is a boundary, not caution: structured context is
// the thing that grows quietly, a company_id today and a candidate name "for
// context" next month. The SDK's setData and addCustomEvent are deliberately
// unused for the same reason.
export function identityOf(user) {
  if (!user || !user.user_id) return null;
  return {
    id: String(user.user_id),
    attributes: {
      name: user.display_name || '',
      email: user.email || '',
      role: user.role || '',
      tenant_id: user.tenant_id || '',
      tenant_name: user.tenant_name || '',
      env: SIGNALCUE_ENV,
    },
  };
}

export default function UserbackProvider({ children }) {
  const location = useLocation();

  // The INITIALISATION PROMISE, not the instance. Two things make this the
  // right ref to hold. React 18 StrictMode double-invokes effects in
  // development (index.js renders under it), and a route change can fire while
  // the first initialisation is still in flight. Holding the promise means
  // every one of those awaits the SAME initialisation instead of starting a
  // second widget.
  const widgetPromise = useRef(null);

  // The last id we told Userback about. identify() is per-SESSION while this
  // effect runs per-route, so without this guard an unchanged session would be
  // re-sent on every navigation.
  const identified = useRef(null);

  useEffect(() => {
    // The entire "staging only" enforcement. No environment check anywhere
    // else -- an unset token means this component is inert.
    if (!USERBACK_TOKEN) return undefined;

    let cancelled = false;

    async function sync() {
      if (!widgetPromise.current) {
        widgetPromise.current = Userback(USERBACK_TOKEN).catch((e) => {
          // A third-party script that fails to load -- blocked, offline,
          // vendor outage -- must not take the app with it. Resolve to null
          // and let every later navigation find nothing to do.
          console.warn('SignalCue: Userback failed to load:', e.message);
          return null;
        });
      }

      const widget = await widgetPromise.current;
      if (cancelled || !widget) return;

      // NO refresh() CALL HERE, deliberately, against the vendor's own SPA
      // guidance -- which says to call it on every route change so the widget
      // notices the new URL. Following that guidance shipped a bug:
      // @userback/widget@0.3.12's refresh() EMPTIES the launcher container and
      // never repopulates it. Measured on staging 2026-08-10, the
      // `.userback-button` count went 1 -> 0 on the call and was still 0 twelve
      // seconds later; showLauncher() did not bring it back.
      //
      // It is the call, not the navigation. refresh() was invoked with no route
      // change at all, on the URL the widget had just drawn itself on, and the
      // launcher died anyway -- so this is not the widget re-evaluating URL
      // targeting rules against an unmatched path.
      //
      // What the user saw was "the widget is missing until I hit reload": a full
      // load re-initialises it, the first in-app navigation destroys it again.
      // It survived initial load only by accident of timing, because that first
      // refresh() lands before the launcher has been drawn.
      //
      // Userback reads window.location when a report is composed, so the URL is
      // picked up without our help. The cost of not calling refresh() is at
      // most a stale URL; the cost of calling it is no widget at all.

      // getUser() never throws and returns null on an unreadable session, which
      // is a real path: AuthHandler writes signalcue_user straight from a URL
      // parameter. No session means an anonymous report, not an error -- the
      // login page is a place a bug can happen.
      const identity = identityOf(authService.getUser());
      if (identity && identity.id !== identified.current) {
        try {
          widget.identify(identity.id, identity.attributes);
          // Recorded only AFTER the call returns. `identified` is what stops an
          // unchanged session being re-sent on every navigation, so recording an
          // id we never successfully sent would make one transient throw
          // permanent: every later navigation would match the guard, skip, and
          // leave the session anonymous for the rest of the page's life.
          identified.current = identity.id;
        } catch (e) {
          console.warn('SignalCue: Userback identify() failed:', e.message);
        }
      }
    }

    sync();
    return () => {
      cancelled = true;
    };
  }, [location]);

  return children;
}
